import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import SaveIcon from '@mui/icons-material/Save';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PropTypes from 'prop-types';

const VideoPreviewCard = ({ video }) => {
  const [hovered, setHovered] = useState(false);
  const [src, setSrc] = useState('');
  const [metadata, setMetadata] = useState({
    name: 'N/A',
    size: 'N/A',
    type: 'N/A',
    dimensions: 'N/A',
    duration: 'N/A',
  });

  useEffect(() => {
    if (!video) {
      setSrc('');
      return;
    }

    let isMounted = true;
    let objectUrl = '';

    if (video instanceof File) {
      objectUrl = URL.createObjectURL(video);
      setSrc(objectUrl);
    } else {
      setSrc(encodeURI(video));
    }

    const updateMetadata = async () => {
      let name = 'N/A';
      let size = 'N/A';
      let type = 'N/A';
      let dimensions = 'N/A';
      let duration = 'N/A';

      const currentSrc = objectUrl || encodeURI(video);

      if (video instanceof File) {
        name = video.name;

        // Format size
        const bytes = video.size;
        if (bytes < 1024 * 1024) {
          size = `${(bytes / 1024).toFixed(1)} Kb`;
        } else {
          size = `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
        }

        // Format type
        if (video.type) {
          const parts = video.type.split('/');
          const rawType = parts[parts.length - 1].toLowerCase();
          if (rawType === 'quicktime') {
            type = 'MOV';
          } else if (rawType === 'x-msvideo') {
            type = 'AVI';
          } else if (rawType === 'x-matroska') {
            type = 'MKV';
          } else {
            type = rawType.toUpperCase();
          }
        } else {
          const ext = video.name.split('.').pop();
          type = ext ? ext.toUpperCase() : 'N/A';
        }
      } else if (typeof video === 'string') {
        // String URL - Extract filename
        try {
          const decoded = decodeURIComponent(video);
          const urlParts = decoded.split('/');
          const filename = urlParts[urlParts.length - 1];
          name = filename.split('?')[0];
        } catch (e) {
          name = video.substring(video.lastIndexOf('/') + 1);
        }

        // Infer type from extension
        const extMatch = name.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1] : '';
        type = ext ? ext.toUpperCase() : 'N/A';

        // Try to fetch content-length for file size
        try {
          const response = await fetch(video, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const bytes = parseInt(contentLength, 10);
            if (bytes < 1024 * 1024) {
              size = `${(bytes / 1024).toFixed(1)} Kb`;
            } else {
              size = `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
            }
          }
        } catch (err) {
          // HEAD request might fail due to CORS, show N/A
        }
      }

      // Load duration and dimensions
      try {
        const videoObj = document.createElement('video');
        videoObj.src = currentSrc;
        videoObj.preload = 'metadata';
        await new Promise((resolve) => {
          videoObj.onloadedmetadata = () => {
            dimensions = `${videoObj.videoWidth} × ${videoObj.videoHeight}`;
            const dur = Math.round(videoObj.duration);
            duration = `${dur} sec`;
            resolve();
          };
          videoObj.onerror = () => resolve();
          // Safety timeout
          setTimeout(resolve, 3000);
        });
      } catch (err) {
        console.error("Failed to load video metadata", err);
      }

      if (isMounted) {
        setMetadata({
          name,
          size,
          type,
          dimensions,
          duration,
        });
      }
    };

    updateMetadata();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [video]);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '200px',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#000000',
      }}
    >
      <video
        src={src}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'filter 0.3s ease',
          filter: hovered ? 'blur(4px)' : 'none',
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          padding: '16px',
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          color: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        {/* Title / File Name Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
          <InsertDriveFileIcon sx={{ fontSize: '18px', color: '#77CFFE' }} />
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
            title={metadata.name}
          >
            {metadata.name}
          </Typography>
        </Box>
        
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', my: 0.5 }} />

        {/* File Size Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SaveIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>File size</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.size}</Typography>
        </Box>

        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* File Type Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <OndemandVideoIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>File type</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.type}</Typography>
        </Box>

        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* Dimensions Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AspectRatioIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>Dimensions</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.dimensions}</Typography>
        </Box>
        
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* Duration Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AccessTimeIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>Duration</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.duration}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

VideoPreviewCard.propTypes = {
  video: PropTypes.any,
};

export default VideoPreviewCard;
